import { ChangeDetectorRef, Component, HostListener, OnInit, ViewChild, ViewEncapsulation } from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import { HttpClient } from '@angular/common/http'
import { MatDialog } from '@angular/material/dialog'
import { ToastrService } from 'ngx-toastr'
import { firstValueFrom } from 'rxjs'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { UserReviewClient } from 'src/app/shared/services/ppv.api'
import {
   ReviewAssessmentSectionsComponent,
   ReviewTemplate,
} from '../components/review-assessment-sections/review-assessment-sections.component'
import { ConfirmDialogComponent } from '../components/confirm-dialog.component'
import {
   PeerInputDetail,
   PeerInputRequest,
   apiBase,
   mapAdditionalResponsibilities,
   revieweeRoleLabel,
   templateForReviewType,
} from '../peer-input'

dayjs.extend(utc)

/**
 * The page a colleague opens from the Peer Input email.
 * It shows a blank copy of the manager's side of the review, using the same
 * review-assessment-sections component the review page uses.
 */
@Component({
   selector: 'peer-input-page',
   templateUrl: './peer-input-page.component.html',
   // Reuse the review page's styles so the questions look identical
   styleUrls: ['../dvm-tool-plan.component.scss', './peer-input-page.component.scss'],
   standalone: false,
   encapsulation: ViewEncapsulation.None,
})
export class PeerInputPageComponent implements OnInit {
   @ViewChild('peerSections') peerSections?: ReviewAssessmentSectionsComponent

   loading = true
   errorMessage = ''
   isSaving = false
   dirty = false
   private hydrating = false

   request: PeerInputDetail | null = null
   template: ReviewTemplate | null = null
   userReview: any = {}
   additionalResponsibilities: any[] = []

   constructor(
      private route: ActivatedRoute,
      private http: HttpClient,
      private toastr: ToastrService,
      private dialog: MatDialog,
      private cdr: ChangeDetectorRef,
      private userReviewClient: UserReviewClient
   ) {}

   private get base(): string {
      return apiBase(this.userReviewClient)
   }

   get canEdit(): boolean {
      return !!this.request?.isPeer && this.request.status !== 'Submitted'
   }

   get roleLabel(): string {
      return revieweeRoleLabel(this.request?.reviewType ?? '')
   }

   ngOnInit(): void {
      const id = Number(this.route.snapshot.paramMap.get('id'))
      if (!id) {
         this.loading = false
         this.errorMessage = 'This link is not valid.'
         return
      }
      this.load(id)
   }

   private load(id: number): void {
      this.loading = true
      this.http.get<PeerInputDetail>(`${this.base}/api/userreview/peer-input/${id}`).subscribe({
         next: (detail) => {
            this.request = detail
            this.template = templateForReviewType(detail.reviewType)
            this.userReview = { revieweeName: detail.revieweeName, reviewType: detail.reviewType }
            this.additionalResponsibilities = mapAdditionalResponsibilities(
               detail.additionalResponsibilities,
               detail.revieweeUserId,
               detail.reviewType
            )
            this.loading = false
            this.cdr.detectChanges()

            // Put any saved draft (or the submitted answers) back on the form
            if (detail.responses?.length) {
               setTimeout(() => {
                  this.hydrating = true
                  this.peerSections?.hydrateFromResponses(detail.responses, 'reviewer')
                  this.hydrating = false
               }, 0)
            }
         },
         error: (err) => {
            this.loading = false
            this.errorMessage =
               err?.status === 403
                  ? 'This request belongs to someone else.'
                  : err?.status === 404
                    ? 'This request was cancelled or no longer exists.'
                    : 'We could not load this request. Please try again.'
         },
      })
   }

   onDataChanged(changes: any): void {
      Object.assign(this.userReview, changes)
      if (!this.hydrating) this.dirty = true
   }

   formatDate(value: string | null | undefined): string {
      return value ? dayjs.utc(value).local().format('MM/DD/YYYY h:mm A') : ''
   }

   async save(isSubmit: boolean): Promise<void> {
      if (!this.request || !this.canEdit || this.isSaving) return

      const responses = this.peerSections?.exportResponses('reviewer') ?? []

      if (isSubmit) {
         if (!responses.length) {
            this.toastr.error('Answer at least one question before submitting.')
            return
         }

         const confirmed = await firstValueFrom(
            this.dialog
               .open(ConfirmDialogComponent, {
                  width: '520px',
                  disableClose: true,
                  data: {
                     title: 'Submit your input?',
                     message: `${this.request.requestedByName} will be able to see your answers and copy any of them into their review. You won't be able to change them after you submit.`,
                  },
               })
               .afterClosed()
         )
         if (!confirmed) return
      }

      this.isSaving = true
      this.http
         .post<PeerInputRequest>(`${this.base}/api/userreview/peer-input/${this.request.id}/save`, { responses, isSubmit })
         .subscribe({
            next: (updated) => {
               this.isSaving = false
               this.dirty = false
               this.request = { ...this.request!, ...updated }
               this.toastr.success(
                  isSubmit ? `Thank you! ${this.request.requestedByName} can now see your answers.` : 'Your draft was saved.'
               )
               if (isSubmit) window.scrollTo({ top: 0, behavior: 'smooth' })
            },
            error: (err) => {
               this.isSaving = false
               const text = typeof err?.error === 'string' ? err.error : ''
               this.toastr.error(text && text.length < 200 ? text : 'Save failed. Please try again.')
            },
         })
   }

   @HostListener('window:beforeunload', ['$event'])
   warnBeforeLeaving(event: BeforeUnloadEvent): void {
      if (this.dirty && this.canEdit) {
         event.preventDefault()
         event.returnValue = ''
      }
   }
}
